/**
 * src/pages/public/ConsentSign.jsx — the client-facing signing page.
 *
 * No login, no account, no app. The client taps a link in an email and signs.
 * Everything this page needs comes from GET /api/consent/form/:token, and the
 * token in the URL is the only credential.
 *
 * The one piece of deliberate friction: the signature pad stays disabled until
 * the client has scrolled the terms to the bottom. A consent form nobody read
 * is worth very little if it is ever challenged, and "I couldn't sign until I
 * scrolled" is a far better record than a tick box.
 *
 * Three terminal states the page must handle gracefully, because they are what
 * a real client will hit: expired link, already signed, and success.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { useApi } from '../../lib/useApi.js';
import SignaturePad from '../../components/SignaturePad.jsx';
import { ErrorBanner, Loader } from '../../components/ui.jsx';

export default function ConsentSign() {
  const { token } = useParams();
  const { data, error, loading } = useApi(`/api/consent/form/${token}`);

  const padRef = useRef(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);

  // The studio's accent colour themes this page — it is the studio's form, not
  // ours. Set on :root so the existing CSS variables pick it up.
  useEffect(() => {
    if (data?.studio?.accentColor) {
      document.documentElement.style.setProperty('--accent', data.studio.accentColor);
    }
    return () => document.documentElement.style.removeProperty('--accent');
  }, [data?.studio?.accentColor]);

  // Prefill the name from the client record, since we already know it.
  useEffect(() => {
    if (data?.client?.name) setSignerName((current) => current || data.client.name);
  }, [data?.client?.name]);

  /**
   * Enables the signature pad once the terms have been read to the bottom.
   *
   * The 8px tolerance covers fractional scroll heights on high-DPI screens,
   * where scrollTop + clientHeight never quite equals scrollHeight.
   *
   * @param {React.UIEvent<HTMLDivElement>} e
   */
  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setScrolledToEnd(true);
  };

  // A short form on a big screen may not scroll at all; measure once it is
  // rendered and unlock immediately if there is nothing to scroll.
  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 8) setScrolledToEnd(true);
  }, [data?.consentText]);

  const submit = async (e) => {
    e.preventDefault();
    const signatureDataUrl = padRef.current?.toDataURL();
    if (!signatureDataUrl) {
      setSubmitError('Please draw your signature.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await api.post(`/api/consent/sign/${token}`, {
        signerName: signerName.trim(),
        signatureDataUrl,
      });
      setResult(res);
    } catch (err) {
      setSubmitError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="public-page">
      <Loader loading={loading} error={error}>
        {data ? (
          <>
            <header className="public-header">
              {data.studio.logoUrl ? <img src={data.studio.logoUrl} alt="" /> : null}
              <h1>{data.studio.name}</h1>
            </header>

            {result ? (
              <SignedConfirmation pdfUrl={result.pdfUrl} time={data.appointment.formattedTime} />
            ) : data.status === 'expired' ? (
              <ExpiredNotice contactEmail={data.studio.contactEmail} />
            ) : data.status === 'signed' ? (
              <SignedConfirmation pdfUrl={data.pdfUrl} time={data.appointment.formattedTime} already />
            ) : (
              <form className="card stack-lg" onSubmit={submit}>
                <div>
                  <h2>Your appointment</h2>
                  <p className="muted">
                    {data.appointment.formattedTime}
                    {data.artist.name ? ` with ${data.artist.name}` : ''}
                  </p>
                </div>

                <div>
                  <h2 style={{ marginBottom: 8 }}>Please read this in full</h2>
                  <div className="consent-scroll" ref={scrollRef} onScroll={onScroll}>
                    {data.consentText}
                  </div>
                  <p className="muted small" style={{ marginTop: 6 }}>
                    {scrolledToEnd
                      ? 'Thanks — you can sign below.'
                      : 'Scroll to the end of the form to continue.'}
                  </p>
                </div>

                <div>
                  <label htmlFor="signerName">Your full name</label>
                  <input
                    id="signerName"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    required
                    minLength={2}
                    autoComplete="name"
                  />
                </div>

                <div>
                  <label htmlFor="signature">Signature</label>
                  <SignaturePad
                    ref={padRef}
                    disabled={!scrolledToEnd}
                    onChange={setHasInk}
                  />
                </div>

                <ErrorBanner>{submitError}</ErrorBanner>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting || !scrolledToEnd || !hasInk || signerName.trim().length < 2}
                >
                  {submitting ? 'Submitting…' : 'Sign and submit'}
                </button>

                <p className="muted small">
                  By signing you confirm the statements above are true. We record the time and your
                  IP address alongside your signature, and store a signed PDF copy.
                </p>
              </form>
            )}
          </>
        ) : null}
      </Loader>
    </div>
  );
}

/**
 * Terminal state: signed.
 *
 * @param {{ pdfUrl: string|null, time: string, already?: boolean }} props
 * @returns {JSX.Element}
 */
function SignedConfirmation({ pdfUrl, time, already = false }) {
  return (
    <div className="card stack center">
      <h2>{already ? 'This form is already signed' : 'All done — thank you'}</h2>
      <p className="muted">
        {already
          ? 'Nothing more to do. We have your consent form on file.'
          : `Your consent form is signed and on file. See you on ${time}.`}
      </p>
      {pdfUrl ? (
        <p>
          <a className="btn btn-primary" href={pdfUrl} target="_blank" rel="noreferrer">
            Download your copy
          </a>
        </p>
      ) : null}
      <p className="muted small">We have also emailed you a copy.</p>
    </div>
  );
}

/**
 * Terminal state: link expired.
 *
 * @param {{ contactEmail: string }} props
 * @returns {JSX.Element}
 */
function ExpiredNotice({ contactEmail }) {
  return (
    <div className="card stack center">
      <h2>This link has expired</h2>
      <p className="muted">
        Consent links are only valid for 48 hours. Ask the studio to send you a new one — it takes
        them a couple of seconds.
      </p>
      <p>
        <a className="btn" href={`mailto:${contactEmail}`}>
          Email {contactEmail}
        </a>
      </p>
    </div>
  );
}
